import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, TrendingDown } from "lucide-react";

function StageBar({ stages, color, worst, testid }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div className="space-y-2" data-testid={testid}>
      {stages.map((s, i) => (
        <div key={s.event} className={`border p-3 ${s.label === worst ? "border-red-700 bg-red-950/30" : "border-neutral-800 bg-appcard"}`}
          data-testid={`${testid}-stage-${s.event}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-300">{i + 1}. {s.label}</span>
            <span className="flex items-center gap-3">
              {s.pct_of_prev !== null && (
                <span className={`text-[10px] tracking-widest ${s.pct_of_prev < 25 ? "text-red-400" : "text-neutral-500"}`}>
                  {s.pct_of_prev}% of prev
                </span>
              )}
              <span className="heading text-base text-white">{s.count.toLocaleString()}</span>
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-neutral-900">
            <div className="h-full" style={{ width: `${Math.max(2, (s.count / max) * 100)}%`, background: color }} />
          </div>
          {s.label === worst && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] tracking-widest text-red-400">
              <TrendingDown size={11} /> BIGGEST DROP-OFF
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Funnel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/admin/funnel?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
  }, [days]);

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-funnel-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest text-volt">ADMIN HQ · GROWTH</div>
          <h1 className="heading text-4xl mt-1">CONVERSION FUNNEL</h1>
          <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
            Event-tracked journeys from homepage visit to paid, with conversion between every stage. Red = your biggest leak.
          </p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`funnel-days-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-[10px] tracking-widest border ${days === d ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-500"}`}>
              {d}D
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="mt-10 text-neutral-500 text-sm" data-testid="funnel-loading">Loading funnel…</div>
      ) : (
        <>
          <div className="mt-8 grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="heading text-lg text-blaze">COACHES</h2>
              <div className="mt-3"><StageBar stages={data.coach} color="#FF3B30" worst={data.coach_worst_stage} testid="funnel-coach" /></div>
            </div>
            <div>
              <h2 className="heading text-lg text-volt">PROGRAMS</h2>
              <div className="mt-3"><StageBar stages={data.program} color="#5B8C2A" worst={data.program_worst_stage} testid="funnel-program" /></div>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="heading text-lg">DATABASE GROUND TRUTH (ALL-TIME)</h2>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="funnel-ground-truth">
              {Object.entries(data.ground_truth).map(([k, v]) => (
                <div key={k} className="border border-neutral-800 bg-appcard p-4">
                  <div className="text-[10px] tracking-widest text-neutral-500">{k.replace(/_/g, " ").toUpperCase()}</div>
                  <div className="heading text-2xl mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 border border-neutral-800 bg-[#0C0C0E] p-4 text-xs text-neutral-500 flex gap-2">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            Event tracking started with the homepage rebuild — stages will fill in as real traffic flows. Database ground truth covers all time.
          </div>
        </>
      )}
    </div>
  );
}
